import { useMutation } from "@apollo/client"
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { Delete, Euro } from "@mui/icons-material"
import { Box, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { DELETE_EXAMINATION } from "graphql/queries"
import { Device, DeviceEutGroup, DeviceType, Examination, Project, Status } from "graphql/types"
import { enqueueSnackbar } from "notistack"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { getDeviceTypeIcon } from "utils/devices"
import { calculateCost } from "utils/generateProjectCalculation"
import { getOperatingModes } from "utils/getOperatingModes"
import { getColorForStatus, getTextForStatus } from "utils/status"
import {
  createNewTestSetup,
  groupDevicesIntoTestSetups,
  removeDeviceFromTestSetup,
  sortGroupedDevices,
  type TestSetup,
} from "utils/testSetupGrouping"
import { v4 } from "uuid"
import { TestSetupCalculation } from "./TestSetupCalculation"

type TestSetupPlanTabProps = {
  project: Project
  // eslint-disable-next-line no-unused-vars
  onSave: (arg: Device, isOffer?: boolean, noSnackbar?: boolean) => void
  onProjectRefresh?: () => void
  projectSalesQuote: string
  onChangesStateChange?: (hasChanges: boolean) => void
}

export interface TestSetupPlanTabRef {
  handleSaveAllChanges: () => Promise<void>
  handleCancelAllChanges: () => void
  hasChanges: () => boolean
}

export const TestSetupPlanTab = forwardRef<TestSetupPlanTabRef, TestSetupPlanTabProps>(
  (props, ref) => {
    const { project, onSave, onProjectRefresh, projectSalesQuote, onChangesStateChange } = props

    const [deleteExamination] = useMutation(DELETE_EXAMINATION)

    const [modifiedTestSetups, setModifiedTestSetups] = useState<Map<string, TestSetup[]>>(
      new Map()
    )
    const [modifiedDevicesWithoutExams, setModifiedDevicesWithoutExams] = useState<
      DeviceEutGroup[]
    >([])
    const [originalTestSetups, setOriginalTestSetups] = useState<Map<string, TestSetup[]>>(
      new Map()
    )
    const [originalDevicesWithoutExams, setOriginalDevicesWithoutExams] = useState<
      DeviceEutGroup[]
    >([])

    const [testSetupCalculationOpen, setTestSetupCalculationOpen] = useState(false)
    const [selectedDevices, setSelectedDevices] = useState<DeviceEutGroup[]>([])
    const [selectedExamination, setSelectedExamination] = useState<Examination | null>(null)

    const [openCards, setOpenCards] = useState<Set<string>>(new Set())

    // Expose functions via ref
    useImperativeHandle(ref, () => ({
      handleSaveAllChanges,
      handleCancelAllChanges,
      hasChanges,
    }))

    // Use the utility function instead of the complex useMemo logic
    const { groupedDevices, devicesWithoutExaminations } = useMemo(() => {
      const result = groupDevicesIntoTestSetups(project)
      return {
        groupedDevices: sortGroupedDevices(result.groupedDevices),
        devicesWithoutExaminations: result.devicesWithoutExaminations,
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.devices])

    // Initialize modified test setups and devices without exams
    useEffect(() => {
      const modifiedMap = new Map<string, TestSetup[]>()
      const originalMap = new Map<string, TestSetup[]>()
      const cardKeys: string[] = []

      groupedDevices.forEach((group) => {
        // Use examination name and norm id as key to match the grouping logic
        const key = `${group.examination.name}_${group.examination.norm.id}`
        modifiedMap.set(key, [...group.testSetups])
        originalMap.set(key, [...group.testSetups])
        cardKeys.push(key)
      })

      // Add devices without exams card key
      if (devicesWithoutExaminations.length > 0) {
        cardKeys.push("devices-without-exams")
      }

      setModifiedTestSetups(modifiedMap)
      setOriginalTestSetups(originalMap)
      setModifiedDevicesWithoutExams([...devicesWithoutExaminations])
      setOriginalDevicesWithoutExams([...devicesWithoutExaminations])

      // Open all cards by default
      setOpenCards(new Set(cardKeys))
    }, [groupedDevices, devicesWithoutExaminations])

    const hasChanges = useCallback(() => {
      // Check if any test setups have been modified
      for (const [examinationId, modifiedSetups] of Array.from(modifiedTestSetups.entries())) {
        const originalSetups = originalTestSetups.get(examinationId) || []
        if (modifiedSetups.length !== originalSetups.length) return true

        for (let i = 0; i < modifiedSetups.length; i++) {
          const modified = modifiedSetups[i]
          const original = originalSetups[i]
          if (!original) return true

          if (modified.devices.length !== original.devices.length) return true

          for (const device of modified.devices) {
            if (!original.devices.find((d) => d.id === device.id)) return true
          }
        }
      }

      // Check if devices without exams have been modified
      if (modifiedDevicesWithoutExams.length !== originalDevicesWithoutExams.length) return true

      return false
    }, [
      modifiedTestSetups,
      modifiedDevicesWithoutExams,
      originalTestSetups,
      originalDevicesWithoutExams,
    ])

    // Notify parent about changes state
    useEffect(() => {
      if (onChangesStateChange) {
        onChangesStateChange(hasChanges())
      }
    }, [hasChanges, onChangesStateChange])

    const handleCancelAllChanges = () => {
      // Reset modified test setups to original state
      setModifiedTestSetups(new Map(originalTestSetups))
      setModifiedDevicesWithoutExams([...originalDevicesWithoutExams])
    }

    const handleSaveAllChanges = async () => {
      // Remove empty test setups from modifiedTestSetups
      const cleanedTestSetups = new Map<string, TestSetup[]>()
      const removedTestSetups: Array<{
        examinationKey: string
        testSetup: TestSetup
        examination: Examination
      }> = []

      for (const [examinationKey, testSetups] of Array.from(modifiedTestSetups.entries())) {
        const nonEmptyTestSetups = testSetups.filter((testSetup) => testSetup.devices.length > 0)

        // Collect removed test setups for logging
        testSetups.forEach((testSetup) => {
          if (testSetup.devices.length === 0) {
            removedTestSetups.push({
              examinationKey,
              testSetup,
              examination: testSetup.examinations[0],
            })
          }
        })

        if (nonEmptyTestSetups.length > 0) {
          cleanedTestSetups.set(examinationKey, nonEmptyTestSetups)
        }
      }

      // Log removed test setups to console
      if (removedTestSetups.length > 0) {
        const removedExaminationIds = removedTestSetups.map(({ examination }) => examination.id)
        await deleteExamination({
          variables: {
            ids: removedExaminationIds,
          },
        })
      }

      // Update the state with cleaned test setups
      setModifiedTestSetups(cleanedTestSetups)

      // Update original test setups to match the saved state
      setOriginalTestSetups(cleanedTestSetups)
      setOriginalDevicesWithoutExams([...modifiedDevicesWithoutExams])

      // Create a map to track which examinations each device should have
      const deviceExaminationsMap = new Map<string, Examination[]>()

      // Add EUTs from test setups and collect their examinations
      for (const testSetups of Array.from(cleanedTestSetups.values())) {
        for (const testSetup of testSetups) {
          for (const device of testSetup.devices) {
            // Initialize device examinations if not exists
            if (!deviceExaminationsMap.has(device.id)) {
              deviceExaminationsMap.set(device.id, [])
            }

            // Add examination to device if not already present
            const deviceExaminations = deviceExaminationsMap.get(device.id)!
            // Find the correct examination for this device - it should have the device's operating modes
            const examination = testSetup.examinations[0]
            // Create a device-specific examination with the device's operating modes
            const deviceSpecificExamination = {
              ...examination,
              operatingModes:
                device.examinations.find(
                  (exam) => exam.name === examination.name && exam.norm.id === examination.norm.id
                )?.operatingModes || examination.operatingModes,
            }
            if (!deviceExaminations.find((ex) => ex.id === deviceSpecificExamination.id)) {
              deviceExaminations.push(deviceSpecificExamination)
            }
          }
        }
      }

      // Add EUTs from devices without exams (they have their modified examinations)
      for (const device of modifiedDevicesWithoutExams) {
        // Use the device's current examinations (which may have been modified)
        deviceExaminationsMap.set(device.id, device.examinations || [])
      }

      // Find devices that have actually changed
      const changedDevices: DeviceEutGroup[] = []

      for (const [deviceId, examinations] of Array.from(deviceExaminationsMap.entries())) {
        // Find the original device to get its other properties
        let originalDevice: DeviceEutGroup | undefined

        // First, look in the original project data
        originalDevice = project.devices.find((d) => d.id === deviceId) as DeviceEutGroup

        // If not found in project, look in test setups
        if (!originalDevice) {
          for (const testSetups of Array.from(cleanedTestSetups.values())) {
            for (const testSetup of testSetups) {
              const found = testSetup.devices.find((d) => d.id === deviceId)
              if (found) {
                originalDevice = found
                break
              }
            }
            if (originalDevice) break
          }
        }

        // If not found in test setups, look in original devices without exams
        if (!originalDevice) {
          originalDevice = originalDevicesWithoutExams.find((d) => d.id === deviceId)
        }

        if (originalDevice) {
          // Check if examinations have actually changed
          const originalExaminations = originalDevice.examinations || []
          const examinationsChanged =
            originalExaminations.length !== examinations.length ||
            originalExaminations.some((origExam, index) => {
              const newExam = examinations[index]
              return !newExam || origExam.id !== newExam.id
            })

          if (examinationsChanged) {
            // Create updated device with new examinations
            const updatedDevice: DeviceEutGroup = {
              ...originalDevice,
              examinations: examinations,
            }
            changedDevices.push(updatedDevice)
          }
        }
      }

      // Only save devices that have actually changed
      if (changedDevices.length > 0) {
        const savePromises = changedDevices.map(async (eut) => {
          try {
            await onSave(eut, false, true) // Pass noSnackbar = true to suppress individual success messages
          } catch (error) {
            console.error("Error saving device:", error)
            throw error
          }
        })

        // Wait for all saves to complete before refreshing
        Promise.all(savePromises)
          .then(() => {
            // Show success message only once
            enqueueSnackbar("TestSetup-Änderungen erfolgreich übernommen", {
              variant: "success",
            })
            // After all saves are complete, refresh the project data from the backend
            if (onProjectRefresh) {
              onProjectRefresh()
            }
          })
          .catch((error) => {
            console.error("Error saving devices:", error)
            enqueueSnackbar(
              "Fehler beim Speichern der Änderungen. Bitte versuchen Sie es erneut.",
              {
                variant: "error",
              }
            )
          })
      } else {
        // No changes to save
        enqueueSnackbar("Keine Änderungen zu speichern", {
          variant: "info",
        })
      }
    }

    const handleDragEnd = (result: DropResult) => {
      if (!result.destination) return

      const { source, destination } = result
      const draggableId = result.draggableId

      // Prevent dragging from "Geräte ohne Prüfung"
      if (source.droppableId === "devices-without-exams") {
        return
      }

      // Handle devices between test setups
      const [sourceExaminationKey, sourceTestSetupIndex] = source.droppableId.split("&")
      const [destExaminationKey, destTestSetupIndex] = destination.droppableId.split("&")

      // Check if source test setup is editable (status must be OPEN)
      const sourceTestSetupsForCheck = modifiedTestSetups.get(sourceExaminationKey) || []
      const sourceTestSetupForCheck = sourceTestSetupsForCheck[parseInt(sourceTestSetupIndex)]

      if (
        sourceTestSetupForCheck &&
        sourceTestSetupForCheck.examinations.length > 0 &&
        sourceTestSetupForCheck.examinations[0].status !== Status.OPEN
      ) {
        enqueueSnackbar(
          `Geräte können nicht verschoben werden, wenn der TestSetup-Status nicht "Offen" ist.`,
          { variant: "warning" }
        )
        return
      }

      // Check if destination test setup is editable (status must be OPEN) - only if not creating new test setup
      if (destTestSetupIndex !== "new") {
        const destTestSetupsForCheck = modifiedTestSetups.get(destExaminationKey) || []
        const destTestSetupForCheck = destTestSetupsForCheck[parseInt(destTestSetupIndex)]

        if (
          destTestSetupForCheck &&
          destTestSetupForCheck.examinations.length > 0 &&
          destTestSetupForCheck.examinations[0].status !== Status.OPEN
        ) {
          enqueueSnackbar(
            `Geräte können nicht zu einem TestSetup hinzugefügt werden, dessen Status nicht "Offen" ist.`,
            { variant: "warning" }
          )
          return
        }
      }

      // Check if creating a new test setup
      if (destTestSetupIndex === "new") {
        // Extract device ID from draggableId (format: "{examinationKey}&{testSetupIndex}&{deviceId}")
        const deviceId = draggableId.split("&").slice(2).join("&")

        // Find the device to move
        const sourceTestSetups = modifiedTestSetups.get(sourceExaminationKey) || []
        const sourceTestSetup = sourceTestSetups[parseInt(sourceTestSetupIndex)]
        const deviceToMove = sourceTestSetup?.devices.find((d) => d.id === deviceId)

        if (!deviceToMove) return

        // Get the examination for the new test setup
        const group = groupedDevices.find(
          (g) => `${g.examination.name}_${g.examination.norm.id}` === destExaminationKey
        )
        if (!group) return

        // Only allow moving within the same examination
        if (sourceExaminationKey === destExaminationKey) {
          // Create new test setup with the device using utility function
          const newTestSetup = createNewTestSetup(group.examination, deviceToMove)

          // Update state
          setModifiedTestSetups((prev) => {
            const newMap = new Map(prev)

            // Create a single updated array that handles both removal and addition
            const examinationTestSetups = modifiedTestSetups.get(sourceExaminationKey) || []
            const updatedExaminationTestSetups = [...examinationTestSetups, newTestSetup].map(
              (testSetup, index) => {
                if (index === parseInt(sourceTestSetupIndex)) {
                  // Remove from source using utility function
                  return removeDeviceFromTestSetup(testSetup, deviceId)
                }
                return testSetup
              }
            )
            newMap.set(sourceExaminationKey, updatedExaminationTestSetups)

            return newMap
          })
        } else {
          // Different examinations: Show error message
          enqueueSnackbar(
            `Das Gerät "${deviceToMove.name}" kann nur innerhalb derselben Prüfung verschoben werden.`,
            { variant: "warning" }
          )
          return
        }
        return
      }

      // Extract device ID from draggableId (format: "{examinationKey}&{testSetupIndex}&{deviceId}")
      const deviceId = draggableId.split("&").slice(2).join("&")

      // Find the device to move
      const sourceTestSetups = modifiedTestSetups.get(sourceExaminationKey) || []
      const sourceTestSetup = sourceTestSetups[parseInt(sourceTestSetupIndex)]
      const deviceToMove = sourceTestSetup?.devices.find((d) => d.id === deviceId)

      if (!deviceToMove) return

      // Only allow moving within the same examination
      if (sourceExaminationKey === destExaminationKey) {
        // Check if source and destination are the same test setup
        if (sourceTestSetupIndex === destTestSetupIndex) {
          // Don't allow moving to the same test setup
          return
        }

        // Update state
        setModifiedTestSetups((prev) => {
          const newMap = new Map(prev)

          // Create a single updated array that handles both removal and addition
          const examinationTestSetups = modifiedTestSetups.get(sourceExaminationKey) || []
          const updatedExaminationTestSetups = examinationTestSetups.map((testSetup, index) => {
            if (index === parseInt(sourceTestSetupIndex)) {
              // Remove from source using utility function
              return removeDeviceFromTestSetup(testSetup, deviceId)
            } else if (index === parseInt(destTestSetupIndex)) {
              // Add to destination and reset salesQuote with new ID
              const newDestID = v4()
              // Get operating modes from the device's DeviceDataGroup
              const deviceOperatingModes = getOperatingModes(deviceToMove)
              return {
                ...testSetup,
                devices: [...testSetup.devices, deviceToMove],
                examinations: testSetup.examinations.map((exam) => ({
                  ...exam,
                  id: newDestID, // All examinations get the same new ID
                  salesQuote: "", // Reset salesQuote when device is added
                  operatingModes: deviceOperatingModes, // Assign operating modes from device's DeviceDataGroup
                })),
              }
            }
            return testSetup
          })
          newMap.set(sourceExaminationKey, updatedExaminationTestSetups)
          return newMap
        })
      } else {
        // Different examinations: Show error message
        enqueueSnackbar(
          `Das Gerät "${deviceToMove.name}" kann nur innerhalb derselben Prüfung verschoben werden.`,
          { variant: "warning" }
        )
        return
      }
    }

    const handleRemoveDevice = (
      examinationKey: string,
      testSetupIndex: number,
      deviceId: string
    ) => {
      // Find the device to remove
      const testSetups = modifiedTestSetups.get(examinationKey) || []
      const testSetup = testSetups[testSetupIndex]
      const deviceToRemove = testSetup?.devices.find((d) => d.id === deviceId)

      if (!deviceToRemove) return

      // Check if test setup is editable (status must be OPEN)
      if (testSetup.examinations.length > 0 && testSetup.examinations[0].status !== Status.OPEN) {
        enqueueSnackbar(
          `Geräte können nicht entfernt werden, wenn der TestSetup-Status nicht "Offen" ist.`,
          { variant: "warning" }
        )
        return
      }

      // Remove device from test setup using utility function
      const updatedTestSetups = testSetups.map((testSetup, index) => {
        if (index === testSetupIndex) {
          return removeDeviceFromTestSetup(testSetup, deviceId)
        }
        return testSetup
      })

      // Get the examination that this device is being removed from
      const examinationToRemove = testSetup.examinations[0]

      // Add the modified device to "devices without exams" for tracking
      // This ensures the device is saved with the modified examinations
      setModifiedDevicesWithoutExams((prev) => {
        // Check if device is already in the list and update it
        const existingIndex = prev.findIndex((d) => d.id === deviceToRemove.id)
        if (existingIndex !== -1) {
          // Update existing device by removing the examination
          const updated = [...prev]
          updated[existingIndex] = {
            ...updated[existingIndex],
            examinations: updated[existingIndex].examinations.filter(
              (exam) =>
                !(
                  exam.name === examinationToRemove.name &&
                  exam.norm.id === examinationToRemove.norm.id
                )
            ),
          }
          return updated
        } else {
          // Create new modified device
          const modifiedDevice = {
            ...deviceToRemove,
            examinations: deviceToRemove.examinations.filter(
              (exam) =>
                !(
                  exam.name === examinationToRemove.name &&
                  exam.norm.id === examinationToRemove.norm.id
                )
            ),
          }
          return [...prev, modifiedDevice]
        }
      })

      setModifiedTestSetups((prev) => {
        const newMap = new Map(prev)
        newMap.set(examinationKey, updatedTestSetups)
        return newMap
      })
    }

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 165px)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              gap: 2,
              pb: 2,
            }}
          >
            {Array.from(modifiedTestSetups.entries())
              .map(([examinationKey, testSetups]) => {
                const group = groupedDevices.find(
                  (g) => `${g.examination.name}_${g.examination.norm.id}` === examinationKey
                )
                if (!group) return null

                return {
                  examinationKey,
                  testSetups,
                  group,
                  sortKey: `${group.examination.name}_${group.examination.norm.name}`,
                }
              })
              .filter((item): item is NonNullable<typeof item> => item !== null)
              .sort((a, b) => {
                // First sort by examination name
                const examComparison = a.group.examination.name.localeCompare(
                  b.group.examination.name
                )
                if (examComparison !== 0) {
                  return examComparison
                }
                // Then sort by norm name
                return a.group.examination.norm.name.localeCompare(b.group.examination.norm.name)
              })
              .map(({ examinationKey, testSetups, group }) => (
                <Box key={examinationKey}>
                  <CollapsibleCard
                    open={openCards.has(examinationKey)}
                    onToggle={() => {
                      setOpenCards((prev) => {
                        const newSet = new Set(prev)
                        if (newSet.has(examinationKey)) {
                          newSet.delete(examinationKey)
                        } else {
                          newSet.add(examinationKey)
                        }
                        return newSet
                      })
                    }}
                    title={`Prüfung ${group.examination.name}`}
                    subheader={`nach ${group.examination.norm.name} • ${testSetups.length} ${testSetups.length > 1 ? "Testsetups" : "Testsetup"} • ${testSetups.reduce((total, setup) => total + setup.devices.length, 0)} Geräte`}
                  >
                    <CardContent>
                      {testSetups.map((testSetup, index) => (
                        <Box
                          key={`${testSetup.examinations[0]?.name}_${testSetup.examinations[0]?.norm.id}_${index}`}
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Typography variant="h6">Testsetup {index + 1}</Typography>
                              <Chip
                                label={getTextForStatus(testSetup.examinations[0].status)}
                                color={getColorForStatus(testSetup.examinations[0].status) as any}
                                size="small"
                                variant="outlined"
                              />
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              {testSetup.examinations[0].salesQuote !== projectSalesQuote && (
                                <Typography variant="body2" color="text.secondary">
                                  Abweichend vom Angebot
                                </Typography>
                              )}

                              <Typography>
                                {
                                  calculateCost(
                                    testSetup.examinations[0].calculation,
                                    testSetup.examinations[0].numberOfRuns || 0
                                  ).price
                                }{" "}
                                €
                              </Typography>
                              <Tooltip
                                title={
                                  hasChanges()
                                    ? "Bitte die Änderungen an den Testsetups übernehmen."
                                    : "Kalkulation und Betriebsmodi für dieses TestSetup bearbeiten."
                                }
                              >
                                <span>
                                  <StyledButton
                                    buttonType="custom"
                                    customIcon={<Euro />}
                                    iconOnly={true}
                                    disabled={hasChanges()}
                                    onClick={() => {
                                      setSelectedDevices(testSetup.devices)
                                      setSelectedExamination(testSetup.examinations[0])
                                      setTestSetupCalculationOpen(true)
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>

                          <Droppable droppableId={`${examinationKey}&${index}`}>
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                  mt: 1,
                                  minHeight: 50,
                                }}
                              >
                                {testSetup.devices.map((device, deviceIndex) => {
                                  const isTestSetupEditable =
                                    testSetup.examinations.length > 0 &&
                                    testSetup.examinations[0].status === Status.OPEN
                                  return (
                                    <Draggable
                                      key={`${examinationKey}&${index}&${device.id}`}
                                      draggableId={`${examinationKey}&${index}&${device.id}`}
                                      index={deviceIndex}
                                      isDragDisabled={!isTestSetupEditable}
                                    >
                                      {(provided) => (
                                        <Box
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                        >
                                          <DeviceBox
                                            device={device}
                                            onRemove={() =>
                                              handleRemoveDevice(examinationKey, index, device.id)
                                            }
                                            showActions={isTestSetupEditable}
                                          />
                                        </Box>
                                      )}
                                    </Draggable>
                                  )
                                })}
                                {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </Box>
                      ))}

                      {/* Drop zone for creating new test setup - only show if there are test setups with multiple devices */}
                      {testSetups.some((testSetup) => testSetup.devices.length > 1) && (
                        <Droppable droppableId={`${examinationKey}&new`}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                border: 2,
                                borderStyle: "dashed",
                                borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
                                borderRadius: 1,
                                p: 3,
                                mt: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 80,
                                backgroundColor: snapshot.isDraggingOver
                                  ? "action.hover"
                                  : "transparent",
                              }}
                            >
                              <Typography variant="body1" color="text.secondary">
                                EUT hierher ziehen für neues TestSetup
                              </Typography>
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      )}
                    </CardContent>
                  </CollapsibleCard>
                </Box>
              ))}

            {/* Card for devices without examinations */}
            {(() => {
              const devicesWithoutExams = modifiedDevicesWithoutExams.filter(
                (device) => device.examinations.length === 0
              )
              return (
                devicesWithoutExams.length > 0 && (
                  <Box>
                    <CollapsibleCard
                      open={openCards.has("devices-without-exams")}
                      title={`Geräte ohne Prüfung (${devicesWithoutExams.length} Stück)`}
                      onToggle={() => {
                        setOpenCards((prev) => {
                          const newSet = new Set(prev)
                          if (newSet.has("devices-without-exams")) {
                            newSet.delete("devices-without-exams")
                          } else {
                            newSet.add("devices-without-exams")
                          }
                          return newSet
                        })
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {devicesWithoutExams.map((device) => (
                            <DeviceBox key={device.id} device={device} showActions={false} />
                          ))}
                        </Box>
                      </CardContent>
                    </CollapsibleCard>
                  </Box>
                )
              )
            })()}

            {selectedDevices.length > 0 && selectedExamination && (
              <TestSetupCalculation
                devices={selectedDevices}
                examination={selectedExamination}
                onSave={async (device, isOffer) => {
                  await onSave(device, isOffer)
                  // Refresh project data after saving to update the UI
                  if (onProjectRefresh) {
                    onProjectRefresh()
                  }
                }}
                setOpen={setTestSetupCalculationOpen}
                open={testSetupCalculationOpen}
              />
            )}
          </Box>
        </Box>
      </DragDropContext>
    )
  }
)

const DeviceBox = ({
  device,
  onRemove,
  showActions = true,
}: {
  device: DeviceEutGroup
  onRemove?: () => void
  showActions?: boolean
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {getDeviceTypeIcon(DeviceType.EUT_GROUP)}
        <Stack>
          <Typography>{device.name}</Typography>
          <Typography variant="caption">{device.barcode}</Typography>
        </Stack>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {showActions && onRemove && (
          <IconButton onClick={onRemove} sx={{ minWidth: "auto", p: 0.5 }}>
            <Delete />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}
